import { ApolloQueryResult } from '@apollo/client';
import { AgencyTemplateFragment } from 'graphql/Agency/apollo-graphql-generated/AgencyTemplateFragment';
import { ChatClientMinimalFragment } from 'graphql/Chats/apollo-graphql-generated/ChatClientMinimalFragment';
import { ExternalChatChannelFragment } from 'graphql/Chats/apollo-graphql-generated/ExternalChatChannelFragment';
import { ExternalChatMessageFragment } from 'graphql/Chats/apollo-graphql-generated/ExternalChatMessageFragment';
import { ExternalChatMessagesLastOfAllClientsQuery, ExternalChatMessagesLastOfAllClientsQueryVariables } from 'graphql/Chats/apollo-graphql-generated/ExternalChatMessagesLastOfAllClientsQuery';
import { ChatOnlineSettingsFilters } from 'graphql/User/UserSettings';
import { UserMinimalFragment } from 'graphql/User/apollo-graphql-generated/UserMinimalFragment';
import { ChatClientTagType } from 'graphql/apollo-graphql-generated/globalTypes';
import { MessageInterface } from 'pages/Chats/ChatOnlinePage/components/MessageList/Messages/Message';
import { ChatClientGroupsData } from 'pages/Chats/ChatOnlinePage/components/hooks/useChatClientGroupsData';
import { ChatClientsData } from 'pages/Chats/ChatOnlinePage/components/hooks/useChatClientsData';
import { create } from 'zustand';

export type ContactModalFormValues = {
  destinationUserId?: string;
  phoneNumber?: string;
  message?: string;
  businessType?: string;
  contactName?: string;
  externalChatChannelId?: string;
  scheduleDate?: Date;
  autoSend?: boolean;
  tagsToAdd?: ChatClientTagType[];
  funnelStepId?: string;
  fromBot?: boolean;
}

type MessageToHighlight = {
  id: string;
  date: Date;
}

export enum ChatType {
  ALL = "ALL",
  UNREAD = "UNREAD",
  GROUP = "GROUP",
  ALERT = "ALERT",
}
const CHAT_TYPE_LOCAL_STORAGE_KEY = 'firefly-dashboard-chat-type'

type RefetchChatListFunction = (variables?: Partial<ExternalChatMessagesLastOfAllClientsQueryVariables>) => Promise<ApolloQueryResult<ExternalChatMessagesLastOfAllClientsQuery>>

type ChatOnlineStore = {
  chatFilters: ChatOnlineSettingsFilters
  setChatFilters: (value: ChatOnlineSettingsFilters) => void

  chatsTypeToShow: ChatType
  setChatsTypeToShow: (value: ChatType) => void

  searchText: string
  setSearchText: (value: string) => void

  selectedClientId: string
  setSelectedClientId: (value: string) => void

  selectedToIdInChannel: string

  selectedExternalChatChannelId: string;
  setSelectedExternalChatChannelId: (value: string) => void

  isGroupSelected: boolean

  isClientSelected: boolean

  isInternalUserSelected: boolean

  chatClientsData: ChatClientsData
  setChatClientsData: (value: ChatClientsData) => void

  chatClientGroupsData: ChatClientGroupsData
  setChatClientGroupsData: (value: ChatClientGroupsData) => void

  newContactModalVisible: boolean
  setNewContactModalVisible: (value: boolean) => void

  newContactModalInitialValues: ContactModalFormValues
  setNewContactModalInitialValues: (value: ContactModalFormValues) => void

  messageToQuote: MessageInterface
  setMessageToQuote: (value: MessageInterface) => void

  messageToHighlight: MessageToHighlight
  setMessageToHighlight: (value: MessageToHighlight) => void

  markedAsUnreadMessageId: string
  setMarkedAsUnreadMessageId: (value: string) => void

  fileUploadModalVisible: boolean
  setFileUploadModalVisible: (value: boolean) => void

  fileUploadModalInitialFiles: File | File[]
  setFileUploadModalInitialFiles: (value: File | File[]) => void

  handleSelectChat: (args: {
    clientId: string,
    externalChatChannelId: string,
    idInChannel: string,
    isGroup: boolean,
    isClient: boolean,
    isInternalUser: boolean,
    messageToHighlight?: MessageToHighlight,
  }) => void

  refetchChatList: RefetchChatListFunction
  setRefetchChatList: (callback: RefetchChatListFunction) => void

  clientIdToSearch: string;
  setClientIdToSearch: (value: string) => void;

  searchClientMessagesDrawerVisible: boolean;
  setSearchClientMessagesDrawerVisible: (value: boolean) => void;

  /** used to trigger rerenders in chat list (e.g. when the back button is pressed in the chat) */
  hideLeftSide: boolean;
  setHideLeftSide: (value: boolean) => void;

  // Caches. Used for setting pre-loaded initial values
  messagesCache?: ExternalChatMessageFragment[];
  setMessagesCache: (messages: ExternalChatMessageFragment[]) => void;

  externalChatChannelsCache?: ExternalChatChannelFragment[];
  setExternalChatChannelsCache: (channels: ExternalChatChannelFragment[]) => void;

  chatClientsCache?: ChatClientMinimalFragment[];
  setChatClientsCache: (clients: ChatClientMinimalFragment[]) => void;

  templatesCache?: AgencyTemplateFragment[];
  setTemplatesCache: (templates: AgencyTemplateFragment[]) => void;

  usersCache?: UserMinimalFragment[];
  setUsersCache: (users: UserMinimalFragment[]) => void;

  setCaches: (args: {
    messagesCache: ExternalChatMessageFragment[],
    externalChatChannelsCache: ExternalChatChannelFragment[],
    chatClientsCache: ChatClientMinimalFragment[],
    templatesCache: AgencyTemplateFragment[],
    usersCache?: UserMinimalFragment[],
  }) => void;

  hasAlertChats: boolean;
  setHasAlertChats: (value: boolean) => void;

  hasGroupUnreadMessages: boolean;
  setHasGroupUnreadMessages: (value: boolean) => void;
};

const useChatOnlineStore = create<ChatOnlineStore>((set) => ({
  chatFilters: null,
  setChatFilters: (value) => set(() => ({ chatFilters: value })),

  chatsTypeToShow: localStorage.getItem(CHAT_TYPE_LOCAL_STORAGE_KEY) as ChatType || ChatType.ALL,
  setChatsTypeToShow: (value) => set(() => {
    localStorage.setItem(CHAT_TYPE_LOCAL_STORAGE_KEY, value)
    return ({ chatsTypeToShow: value })
  }),

  searchText: null,
  setSearchText: (value) => set(() => ({ searchText: value })),

  selectedClientId: null,
  setSelectedClientId: (value) => set(() => ({ selectedClientId: value })),

  selectedToIdInChannel: null,

  selectedExternalChatChannelId: null,
  setSelectedExternalChatChannelId: (value) => set(() => ({ selectedExternalChatChannelId: value })),

  isGroupSelected: null,

  isClientSelected: null,

  isInternalUserSelected: null,

  chatClientsData: null,
  setChatClientsData: (value) => set(() => ({ chatClientsData: value })),

  chatClientGroupsData: null,
  setChatClientGroupsData: (value) => set(() => ({ chatClientGroupsData: value })),

  newContactModalVisible: false,
  setNewContactModalVisible: (value) => set(() => ({ newContactModalVisible: value })),

  newContactModalInitialValues: null,
  setNewContactModalInitialValues: (value) => set(() => ({ newContactModalInitialValues: value })),

  messageToQuote: null,
  setMessageToQuote: (value) => set(() => ({ messageToQuote: value })),

  messageToHighlight: null,
  setMessageToHighlight: (value) => set(() => ({ messageToHighlight: value })),

  markedAsUnreadMessageId: null,
  setMarkedAsUnreadMessageId: (value) => set(() => ({ markedAsUnreadMessageId: value })),

  fileUploadModalVisible: false,
  setFileUploadModalVisible: (value) => set(() => ({ fileUploadModalVisible: value })),

  fileUploadModalInitialFiles: null,
  setFileUploadModalInitialFiles: (value) => set(() => ({ fileUploadModalInitialFiles: value })),

  handleSelectChat: ({ clientId, externalChatChannelId, idInChannel, isClient, isGroup, isInternalUser, messageToHighlight }) => set(() => ({
    selectedClientId: clientId,
    selectedExternalChatChannelId: externalChatChannelId,
    selectedToIdInChannel: isInternalUser ? clientId : idInChannel,
    isClientSelected: isClient,
    isGroupSelected: isGroup,
    isInternalUserSelected: isInternalUser,
    messageToHighlight: messageToHighlight ? messageToHighlight : null,
  })),

  refetchChatList: null,
  setRefetchChatList: (value) => set(() => ({ refetchChatList: value })),

  clientIdToSearch: null,
  setClientIdToSearch: (value) => set(() => ({ clientIdToSearch: value })),

  searchClientMessagesDrawerVisible: null,
  setSearchClientMessagesDrawerVisible: (value) => set(() => ({ searchClientMessagesDrawerVisible: value })),

  setMessagesCache: (value) => set(() => ({ messagesCache: value })),
  setExternalChatChannelsCache: (value) => set(() => ({ externalChatChannelsCache: value })),
  setChatClientsCache: (value) => set(() => ({ chatClientsCache: value })),
  setTemplatesCache: (value) => set(() => ({ templatesCache: value })),
  setUsersCache: (value) => set(() => ({ usersCache: value })),

  hideLeftSide: false,
  setHideLeftSide: (value) => set(() => ({ hideLeftSide: value })),

  setCaches: (args: {
    messagesCache: ExternalChatMessageFragment[],
    externalChatChannelsCache: ExternalChatChannelFragment[],
    chatClientsCache: ChatClientMinimalFragment[],
    templatesCache?: AgencyTemplateFragment[],
    usersCache?: UserMinimalFragment[],
  }) => set(() => ({
    messagesCache: args.messagesCache,
    externalChatChannelsCache: args.externalChatChannelsCache,
    chatClientsCache: args.chatClientsCache,
    templatesCache: args.templatesCache,
    usersCache: args.usersCache,
  })),

  hasAlertChats: false,
  setHasAlertChats: (value) => set(() => ({ hasAlertChats: value })),

  hasGroupUnreadMessages: false,
  setHasGroupUnreadMessages: (value) => set(() => ({ hasGroupUnreadMessages: value })),
}));

export default useChatOnlineStore;
